import React, { FC, useEffect, useMemo, useState } from 'react';
import { useApolloClient, useLazyQuery, useMutation } from '@apollo/react-hooks';
import { useHistory, useParams } from 'react-router-dom';
import {
	Breadcrumbs,
	BreadcrumbsText,
	ExtendedTheme,
	Heading,
	Link,
	Loader,
	Snackbar,
	Tooltip,
	UserInterfaceCheck
} from 'neon-design-system';
import { createStyles, Grid, makeStyles } from '@material-ui/core';
import { ColDef, ColumnApi, GridApi, ICellRendererParams, NewColumnsLoadedEvent, RowNode } from 'ag-grid-community';
import AdminList, { DecisionCellRenderer, LinkCellRenderer, ReadOnlyLabelCellRenderer } from '../../ui/admin/AdminList';
import {
	configureLongDateFormat,
	formatDisplayDateTime,
	formatDisplayDate,
	dateComparator
} from '../../../utils/date.utils';
import { formatPlural, handleEntityPluralCase } from '../../../utils/string.utils';
import { baseUrls } from '../../../utils/baseUrls.utils';
import { GET_PREMIUM_CHANNEL_SUBSCRIBED_USERS_BY_ID, PROCESS_FREE_CLOSED_REQUEST, PROCESS_TRIAL_REQUEST } from '../../../gql/channels';
import { Channel, ChannelState, ChannelSubscription, ChannelSubscriptionType, ChannelTypes, User } from '../../../shared/types';
import { ChannelSubscribersFilter } from '../../ui/admin/ChannelSubscribersFilter';
import { ChannelSubscriberKind, ResearchRole } from '../../../shared/global-enums';
import {
	formatChannelSubscriptionType,
	isUserNotChannelAdmin
} from '../../../utils/channel.utils';
import {
	isNonChargeChannelSubscriptionType,
	isSubscriptionActive,
	isSubscriptionExpired,
	isStripeChannelSubscriptionType,
	isAccountChannelSubscriptionType,
	isTrialChannelSubscriptionType,
	isPaidSubscription,
	isRebatedAccountSubscription,
	isActivePaidSubscription
} from '../../../utils/channelSubscriptions.utils';
import { useAuth0Data } from 'neon-auth0';
import jwt from 'jwt-decode';
import { config } from '../../../config';
import { useUser } from '../../providers/UserProvider';
import SubscriberStatsPanel from './SubscriberStatsPanel';
import { MessageTypes } from '../../providers/NotificationProvider';
import { USER_ARTICLE_VIEWS_BY_CHANNEL } from '../../../gql/users';
import { TextOverlapping } from '../../ui/TextOverlapping';
import { formatAnonymousUserData } from '../../../utils/utils';
import { ChannelLabel } from '../../ui/ChannelLabel';

configureLongDateFormat();

interface PremiumChannelsStatsProps {
	channel: Channel;
}

const useStyles = makeStyles<ExtendedTheme>((theme: ExtendedTheme) => createStyles({
	container: {
		display: 'flex',
		justifyContent: 'center',
		maxWidth: 1200,
		margin: theme.spacing(2, 'auto'),
		paddingLeft: theme.spacing(2),
		paddingRight: theme.spacing(2),
		paddingBottom: theme.spacing(6)
	},
	statsItem: {
		'& > div': {
			maxWidth: '33%'
		}
	},
	pendingTrialHeading: {
		marginTop: theme.spacing(2)
	},
	pendingMargin: {
		marginTop: 0
	},
	overwriteAdminListContainer: {
		marginBottom: theme.spacing(2)
	},
	overwriteAdminListClass: {
		minHeight: theme.spacing(2)
	}
}));

const SubscriberCategoriesStatsBreadcrumbs: FC<PremiumChannelsStatsProps> = ({ channel }) => (
	<Breadcrumbs>
		<Link to={baseUrls.publisher.premiumChannels.list}>Channels</Link>
		<Link to={`${baseUrls.publisher.premiumChannels.list}/edit/${channel._id}`}>
			{channel.name}
		</Link>
		<BreadcrumbsText>Subscriber stats</BreadcrumbsText>
	</Breadcrumbs>
);

const getNodeValue = (node: RowNode, field: string) => node.data?.channelSubscriptions[0]?.[field as any as string]

const createSubscriptionDateComparator = (field: 'createdAt' | 'updatedAt' | 'cancelAt' | 'cancelRequestedAt') =>
	(_a: any, _b: any, nodeA: RowNode, nodeB: RowNode) => {
		const date1 = getNodeValue(nodeA, field);
		const date2 = getNodeValue(nodeB, field);
		return dateComparator(date1, date2);
	}

const ChannelsStatsPage: FC = () => {
	const classes = useStyles();
	const { hasScope } = useUser();
	const { _id } = useParams<{ _id: string }>();
	const history = useHistory();
	const [subscriberFilter, setSubscriberFilter] = useState<ChannelSubscriberKind>(ChannelSubscriberKind.Trial);
	const [gridApi, setGridApi] = useState<GridApi | undefined>(undefined);
	const [columnApi, setColumnApi] = useState<ColumnApi | undefined>(undefined);
	const [approveTrialAlertBox, setApproveTrialAlertBox] = useState<{ visible: boolean; channelTrialDuration?: number }>({
		visible: false
	});
	const [rejectTrialAlertBoxVisible, setRejectTrialAlertBoxVisible] = useState<boolean>(false);
	const [acceptAlertBox, setAcceptAlertBox] = useState({visible: false, username: ''});
	const [rejectAlertBox, setRejectAlertBox] = useState({visible: false, username: ''});
	const [queryString, setQueryString] = useState<string>('');
	const { accessToken } = useAuth0Data();
	const neonUserId = jwt<any>(accessToken)[config.auth0.namespace + 'neonUserId'];
	const isInsightsAdmin = [ResearchRole.Administrator].some(hasScope);
	const channelAdmin = [ResearchRole.Administrator, ResearchRole.Editor].some(hasScope);

	const canBeDeleted = useMemo(() => (channelSubscription: ChannelSubscription) => {
		if (subscriberFilter === ChannelSubscriberKind.Unsubscribed) return false;
		return (!isStripeChannelSubscriptionType(channelSubscription.subscriptionType) &&
			isSubscriptionActive(channelSubscription)) ||
			(isSubscriptionExpired(channelSubscription) && isTrialChannelSubscriptionType(channelSubscription.subscriptionType));
	}, [subscriberFilter]);

	const [findChannel, { data, loading }] = useLazyQuery<{ channel: Channel }>(GET_PREMIUM_CHANNEL_SUBSCRIBED_USERS_BY_ID, {
		fetchPolicy: 'network-only',
		variables: { id: _id }
	});

	const isFreeChannelSubscriptionType = data?.channel?.type !== ChannelTypes.premium;

	const client = useApolloClient();
	const [activeTrialUsersWithViewCount, setActiveTrialUsersWithViewCount] = useState<{[id: string]: number}>({});

	const [processTrialRequest, { loading: processTrialRequestLoading }] = useMutation(PROCESS_TRIAL_REQUEST,
		{
			onCompleted: data => {
				findChannel();
			}
		});

	const [processFreeClosedRequest, { loading: processFreeClosedRequestLoading }] = useMutation(PROCESS_FREE_CLOSED_REQUEST,
		{
			onCompleted: data => {
				findChannel();
			}
		});

	const activeChannelSubscription = useMemo(() => (cs: ChannelSubscription) =>
		cs.channelId === data?.channel._id &&
		cs.subscriptionState === 'active' &&
		cs.subscriptionType === ChannelSubscriptionType.Trial
	, [data?.channel]);
	const isActiveTrialUser = (user: User) => user.channelSubscriptions.some(activeChannelSubscription);

	const makeEditSubscriberURL = (channelId: string, neonUserId: any): string =>
		`${baseUrls.publisher.premiumChannels.subscriberMain}/${channelId}/edit/${encodeURIComponent(neonUserId)}`;

	const gridCallback = (api: GridApi, columnApi: ColumnApi) => {
		setGridApi(api);
		setColumnApi(columnApi);
	}

	useEffect(() => {
		if (data?.channel?.state === ChannelState.Archived) {
			history.push(baseUrls.publisher.premiumChannels.list);
		}
	}, [data?.channel?.state, history]);

	useEffect(() => {
		if (_id) {
			findChannel()
		}
	}, [_id, findChannel]);

	useEffect(() => {
		if (isInsightsAdmin) return;

		if (data && isUserNotChannelAdmin(data.channel.adminUsers, neonUserId)) {
			history.push(baseUrls.publisher.premiumChannels.list)
		}
	}, [data, history, isInsightsAdmin, neonUserId]);

	useEffect(() => {
		if (!gridApi) return;
		gridApi.setQuickFilter(queryString);
	}, [gridApi, queryString]);


	useEffect(() => {
		const trialUsers = data?.channel.channelSubscribedUsers.filter(isActiveTrialUser);
		if (!trialUsers?.length) return;
		Promise.all(trialUsers.map(u => client.query({
			query: USER_ARTICLE_VIEWS_BY_CHANNEL,
			variables: {id: u._id, channelId: data?.channel._id}
		}))).then(viewsData => {
			const map = viewsData.reduce((res, view) => ({...res, [view.data.user._id]: view.data.user.articleViewsByChannel}), {});
			setActiveTrialUsersWithViewCount(map);
		});
	}, [data?.channel.channelSubscribedUsers]);

	useEffect(() => {
		if (data?.channel) {
			setSubscriberFilter(data.channel.type === ChannelTypes.premium ? ChannelSubscriberKind.Trial : ChannelSubscriberKind.All);
		}
	}, [data]);

	const columnDefs: ColDef[] = useMemo(() => [
		{
			headerName: 'User details',
			field: 'name',
			width: 400,
			menuTabs: [],
			cellRendererFramework: (props: any) => {
				const value = formatAnonymousUserData(props.value);
				return channelAdmin && canBeDeleted(props.data.channelSubscriptions[0]) ?
					<LinkCellRenderer
						history={history}
						label={value}
						to={makeEditSubscriberURL(_id, props.data._id)}
					/>
					: <ReadOnlyLabelCellRenderer {...props} value={value} />;
			}
		},
		{
			headerName: 'Company',
			field: 'company',
			cellRendererFramework: (props: { value: string }) => props.value ?? '',
			menuTabs: []
		},
		{
			headerName: 'Email',
			field: 'email',
			width: 300,
			cellRendererFramework: (props: { value: string }) => <TextOverlapping value={formatAnonymousUserData(props.value)} maxLength={30} maxWidth={220} />,
			menuTabs: []
		},
		{
			headerName: 'Date subscribed',
			cellRendererFramework: (props: ICellRendererParams) => {
				const createdAt = props.data?.channelSubscriptions[0]?.createdAt;

				return <>{formatDisplayDate(createdAt, false)}</>
			},
			menuTabs: [],
			comparator: createSubscriptionDateComparator('createdAt')
		},
		{
			headerName: 'Date unsubscribed',
			cellRendererFramework: (props: ICellRendererParams) => {
				const cancelRequestedAt = props.data?.channelSubscriptions[0]?.cancelRequestedAt;
				return <>{formatDisplayDate(cancelRequestedAt, false)}</>
			},
			menuTabs: [],
			hide: subscriberFilter !== ChannelSubscriberKind.Unsubscribed,
			comparator: createSubscriptionDateComparator('cancelRequestedAt')
		},
		{
			headerName: 'Billing End',
			cellRendererFramework: (props: ICellRendererParams) => {
				const cancelAt = props.data?.channelSubscriptions[0]?.cancelAt;
				return <>{formatDisplayDate(cancelAt, false)}</>
			},
			menuTabs: [],
			hide: subscriberFilter !== ChannelSubscriberKind.Unsubscribed || isFreeChannelSubscriptionType,
			comparator: createSubscriptionDateComparator('cancelAt')
		},
		{
			headerName: 'Expiry',
			cellRendererFramework: (props: ICellRendererParams) => {
				const expiryDate = props.data?.channelSubscriptions[0]?.cancelAt;
				if (expiryDate) return <>{formatDisplayDate(expiryDate, false)}</>
				return 'No expiry';
			},
			menuTabs: [],
			hide: ![ChannelSubscriberKind.Expired, ChannelSubscriberKind.NonCharge].includes(subscriberFilter) || isFreeChannelSubscriptionType,
			comparator: createSubscriptionDateComparator('cancelAt')
		},
		{
			headerName: 'Subscription type',
			menuTabs: [],
			hide: isFreeChannelSubscriptionType,
			cellRendererFramework: (props: ICellRendererParams) => {
				const channelSubscription = props.data?.channelSubscriptions[0];
				const isNonCharge = isNonChargeChannelSubscriptionType(channelSubscription?.subscriptionType);
				const notes = channelSubscription?.notes;

				if (isNonCharge && notes) {
					return <Tooltip title={notes}>
						<div>{formatChannelSubscriptionType(props.data?.channelSubscriptions[0])}</div>
					</Tooltip>
				}

				return formatChannelSubscriptionType(props.data?.channelSubscriptions[0])
			},
			comparator: (valueA, valueB, nodeA, nodeB) => {
				const subscriptionType1 = formatChannelSubscriptionType(nodeA.data?.channelSubscriptions[0]);
				const subscriptionType2 = formatChannelSubscriptionType(nodeB.data?.channelSubscriptions[0]);
				return subscriptionType1.localeCompare(subscriptionType2);
			}
		},
		{
			headerName: 'Email notifications',
			cellRendererFramework: (props: ICellRendererParams) => {
				const emailNotificationsEnabled = props.data?.channelSubscriptions[0]?.emailNotifications;
				if (emailNotificationsEnabled) return <UserInterfaceCheck size="medium" />
				return null;
			},
			menuTabs: [],
			hide: [ChannelSubscriberKind.Expired, ChannelSubscriberKind.Unsubscribed].includes(subscriberFilter)
		}
	], [_id, subscriberFilter, channelAdmin, history, data]);

	const [accept, reject] = useMemo(() => {
		if(!data?.channel) return [() => {}, () => {}];

		const makeVariables = (userID: string, status: boolean) => ({
			variables: {
				item: {
					userId: userID,
					channelId: _id,
					approved: status
				}
			}
		})

		if (data.channel.type === ChannelTypes.premium) {
			const onApproveTrialRequestClick = async (userID: string) => {
				const defaultTrialDurationDays = data.channel.defaultTrialDurationDays;
				await processTrialRequest(makeVariables(userID, true));

				setApproveTrialAlertBox({ visible: true, channelTrialDuration: defaultTrialDurationDays || config.trialDays });
			};

			const onRejectTrialRequestClick = async (userID: string) => {
				await processTrialRequest(makeVariables(userID, false));

				setRejectTrialAlertBoxVisible(true);
			};

			return [onApproveTrialRequestClick, onRejectTrialRequestClick];
		}

		const accept = async (userID: string) => {
			const res = await processFreeClosedRequest(makeVariables(userID, true));
			setAcceptAlertBox({visible: true, username: res.data.processFreeClosedRequest.name});
		}
		const reject = async (userID: string) => {
			const res = await processFreeClosedRequest(makeVariables(userID, false));
			setRejectAlertBox({visible: true, username: res.data.processFreeClosedRequest.name});
		}

		return [accept, reject];

	}, [processTrialRequest, data, _id]);

	const pendingColumnDefs: ColDef[] = useMemo(() => [
		{
			headerName: 'User details',
			field: 'name',
			width: 266,
			menuTabs: [],
			cellRendererFramework: (props: any) => {
				const value = formatAnonymousUserData(props.value);
				return channelAdmin && canBeDeleted(props.data.channelSubscriptions[0])
					? <LinkCellRenderer history={history} label={value} to={makeEditSubscriberURL(_id, props.data._id)} />
					: <ReadOnlyLabelCellRenderer {...props} value={value} />;
			}
		},
		{
			headerName: 'Company',
			field: 'company',
			width: 266,
			cellRendererFramework: (props: { value: string }) => props.value ?? '',
			menuTabs: []
		},
		{
			headerName: 'Email',
			field: 'email',
			width: 266,
			cellRendererFramework: (props: { value: string }) => formatAnonymousUserData(props.value),
			menuTabs: []
		},
		{
			headerName: 'Requested on',
			width: 266,
			cellRendererFramework: (props: ICellRendererParams) => {
				const createdAt = props.data?.channelSubscriptions[0]?.createdAt;
				return <>{formatDisplayDateTime(createdAt, false)}</>
			},
			menuTabs: [],
			comparator: createSubscriptionDateComparator('createdAt')
		},
		{
			headerName: 'Action',
			width: 120,
			cellRendererFramework: (props: ICellRendererParams) =>
				<DecisionCellRenderer
					onYesClick={() => accept(props.data._id)}
					onNoClick={() => reject(props.data._id)} />,
			menuTabs: []
		}
	], [_id, channelAdmin, history, data, accept, reject]);

	const trialColumnDefs: ColDef[] = useMemo(() => [
		{
			headerName: 'User details',
			field: 'name',
			width: 197,
			menuTabs: [],
			cellRendererFramework: (props: any) => {
				const value = formatAnonymousUserData(props.value);
				return channelAdmin && canBeDeleted(props.data.channelSubscriptions[0])
					? <LinkCellRenderer history={history} label={value} to={makeEditSubscriberURL(_id, props.data._id)} />
					: <ReadOnlyLabelCellRenderer {...props} value={value} />;
			}
		},
		{
			headerName: 'Company',
			field: 'company',
			width: 197,
			cellRendererFramework: (props: { value: string }) => props.value ?? '',
			menuTabs: []
		},
		{
			headerName: 'Email',
			field: 'email',
			width: 391,
			cellRendererFramework: (props: { value: string }) => formatAnonymousUserData(props.value),
			menuTabs: []
		},
		{
			headerName: 'Trial started',
			width: 100,
			cellRendererFramework: (props: ICellRendererParams) => {
				const updatedAt: Date = props.data?.channelSubscriptions[0]?.updatedAt;
				return <>{formatDisplayDate(updatedAt, false)}</>
			},
			menuTabs: [],
			comparator: createSubscriptionDateComparator('updatedAt')
		},
		{
			headerName: 'Trial ending',
			colId: 'trialEnding',
			width: 100,
			cellRendererFramework: (props: ICellRendererParams) => {
				const expiryDate = props.data?.channelSubscriptions[0]?.cancelAt;
				if (expiryDate) return <>{formatDisplayDate(expiryDate, false)}</>
				return 'No expiry';
			},
			menuTabs: [],
			comparator: createSubscriptionDateComparator('cancelAt')
		},
		{
			headerName: 'Articles read',
			width: 197,
			cellRendererFramework: (props: ICellRendererParams) => {
				const viewsCount = props.data?.articleViewsByChannel || 0;
				if (viewsCount) {
					return formatPlural(viewsCount, 'article')
				}
				return 'No articles';
			},
			menuTabs: []
		}
	], [_id, channelAdmin, history]);

	const defaultSortingCol = useMemo(() => {
		if (!columnApi) return;
		switch (subscriberFilter) {
		case ChannelSubscriberKind.Trial:
			return 'trialEnding';
		default:
			return;
		}
	}, [columnApi, subscriberFilter]);

	useEffect(() => {
		if(!defaultSortingCol || !columnApi) return;

		const state = columnApi.getColumnState();
		if (!state) return;
		columnApi.applyColumnState({
			state: state.map(cs => cs.colId === defaultSortingCol ? {...cs, sort: 'asc'} : cs)
		});

	}, [defaultSortingCol, columnApi]);

	if (!data || loading || processTrialRequestLoading || processFreeClosedRequestLoading || (!isInsightsAdmin && isUserNotChannelAdmin(data.channel.adminUsers, neonUserId))) {
		return <Loader />;
	}

	const channel = data.channel;

	const subscribedUsers = channel.channelSubscribedUsers.filter(user => {
		const subscription = user.channelSubscriptions?.[0];

		switch (subscriberFilter) {
		case ChannelSubscriberKind.Expired:
			return subscription.subscriptionState === 'expired';
		case ChannelSubscriberKind.NonCharge:
			return !isSubscriptionExpired(subscription) && subscription.subscriptionState === 'active' &&
					(isNonChargeChannelSubscriptionType(subscription.subscriptionType) ||
						(isAccountChannelSubscriptionType(subscription.subscriptionType) && subscription.rebate)
					);
		case ChannelSubscriberKind.Paid:
			return isActivePaidSubscription(subscription);
		case ChannelSubscriberKind.Unsubscribed:
			return subscription.subscriptionState === 'canceled' ||
					(
						(isPaidSubscription(subscription) || isRebatedAccountSubscription(subscription)) &&
						!isSubscriptionActive(subscription) && subscription.subscriptionState !== 'expired'
					);
		case ChannelSubscriberKind.PaymentFailed:
			return subscription.subscriptionState === 'paymentFailed';
		case ChannelSubscriberKind.Trial:
			return isTrialChannelSubscriptionType(subscription.subscriptionType);
		case ChannelSubscriberKind.All:
		default:
			return isSubscriptionActive(subscription)
		}
	});

	const pendingSubscription = (cs: ChannelSubscription) => cs.channelId === channel._id && cs.subscriptionState === 'pending';
	const pendingTrialSubscription = (cs: ChannelSubscription) => pendingSubscription(cs) && cs.subscriptionType === ChannelSubscriptionType.Trial;

	const isPendingUser = (user: User) => user.channelSubscriptions.some(pendingSubscription);
	const isPendingTrialUser = (user: User) => user.channelSubscriptions.some(pendingTrialSubscription);

	const sortBySubscriptionDateField = (field: 'createdAt' | 'cancelAt') => (a: User, b: User) => {
		const acs: ChannelSubscription | undefined = a.channelSubscriptions.find(pendingTrialSubscription);
		const bcs: ChannelSubscription | undefined = b.channelSubscriptions.find(pendingTrialSubscription);
		return new Date(acs?.[field] ?? new Date()).getTime() - new Date(bcs?.[field] ?? new Date()).getTime();
	}

	const sortByTrialRequestedAtAsc = sortBySubscriptionDateField('createdAt');
	const sortByTrialEndingAsc = sortBySubscriptionDateField('cancelAt');

	const pendingTrialUsers = subscribedUsers.filter(isPendingTrialUser).sort(sortByTrialRequestedAtAsc);
	const activeTrialUsers = subscribedUsers.filter(isActiveTrialUser).sort(sortByTrialEndingAsc).map(u => ({...u, articleViewsByChannel: activeTrialUsersWithViewCount[u._id] || 0}));

	const pendingUsers = channel.channelSubscribedUsers.filter(isPendingUser).sort(sortByTrialRequestedAtAsc);

	const toolbar = <ChannelSubscribersFilter
		channelType={channel.type}
		channelSubscriberKind={subscriberFilter}
		onQueryStringChange={setQueryString}
		onSelectedKindChange={setSubscriberFilter}
		searchString={queryString}
		onAddNewSubscriber={(): void => {
			history.push(baseUrls.publisher.premiumChannels.addSubscriber.replace(':_id', channel._id));
		}}
		onAddBulkNewSubscribers={(): void => {
			history.push(baseUrls.publisher.premiumChannels.bulkAddSubscribers.replace(':_id', channel._id));
		}}
	/>;

	const gridOptions = {
		onNewColumnsLoaded: (e: NewColumnsLoadedEvent) => e.api.sizeColumnsToFit()
	};

	const isTrial = subscriberFilter === ChannelSubscriberKind.Trial;
	const isFreeClosedAll = subscriberFilter === ChannelSubscriberKind.All && channel.type === ChannelTypes.freeClosed;
	return (
		<div className={classes.container}>
			<Grid container spacing={2}>
				<Snackbar
					message={`The user is now on a ${approveTrialAlertBox.channelTrialDuration}-day trial period, and they’ve been notified via email.`}
					type={MessageTypes.info}
					open={approveTrialAlertBox.visible}
					onClose={() => setApproveTrialAlertBox({ visible: false })}
				/>
				<Snackbar
					message="The trial request has been rejected, and the user has been notified via email."
					type={MessageTypes.info}
					open={rejectTrialAlertBoxVisible}
					onClose={() => setRejectTrialAlertBoxVisible(false)}
				/>
				<Snackbar
					message={`${acceptAlertBox.username} is now a subscriber`}
					type={MessageTypes.info}
					open={acceptAlertBox.visible}
					onClose={() => setAcceptAlertBox({visible: false, username: ''})}
				/>
				<Snackbar
					message={`${rejectAlertBox.username} won't get access to your channel`}
					type={MessageTypes.info}
					open={rejectAlertBox.visible}
					onClose={() => setRejectAlertBox({visible: false, username: ''})}
				/>
				<Grid item xs={12}>
					<SubscriberCategoriesStatsBreadcrumbs channel={channel} />
				</Grid>
				<Grid item xs={12}>
					<Heading styleName="title1">{channel.name}</Heading>
				</Grid>
				<Grid item xs={12}>
					<ChannelLabel type={channel.type} withDescription/>
				</Grid>
				<Grid item xs={12}>
					<SubscriberStatsPanel channel={channel} channelSubscriberKind={subscriberFilter} />
				</Grid>
				{isTrial && <Grid item xs={12}>
					<AdminList
						listPageHeader={true}
						aboveTable={
							pendingTrialUsers.length > 0
								? <Heading styleName="headline1" className={classes.pendingTrialHeading}>Pending requests</Heading>
								: null}
						showHeader={false}
						name={channel.name}
						items={pendingTrialUsers}
						loading={loading}
						columns={pendingColumnDefs}
						showAdd={false}
						apiCallback={gridCallback}
						showCaption={false}
						pagination={false}
						gridOptions={gridOptions}
						overrideContainerClass={classes.overwriteAdminListContainer}
						overrideListClass={classes.overwriteAdminListClass}
						showTableWhenEmpty={false}
						toolbar={toolbar}
					/>
					<Heading styleName="headline1">Users currently on a trial period</Heading>
					<AdminList
						listPageHeader={true}
						entityForCaptionText={handleEntityPluralCase(channel.channelSubscribedUsers?.length, 'user')}
						showHeader={false}
						name={channel.name}
						items={activeTrialUsers}
						loading={loading}
						columns={trialColumnDefs}
						showAdd={false}
						apiCallback={gridCallback}
						overrideListClass={classes.overwriteAdminListClass}
						gridOptions={gridOptions}
					/>

				</Grid>}
				{isFreeClosedAll && <Grid item xs={12}>
					<AdminList
						listPageHeader={true}
						aboveTable={
							pendingUsers.length > 0
								? <Heading styleName="headline1" className={classes.pendingTrialHeading}>Pending requests</Heading>
								: null}
						showHeader={false}
						name={channel.name}
						items={pendingUsers}
						loading={loading}
						columns={pendingColumnDefs}
						showAdd={false}
						apiCallback={gridCallback}
						showCaption={false}
						pagination={false}
						gridOptions={gridOptions}
						overrideContainerClass={classes.overwriteAdminListContainer}
						overrideListClass={classes.overwriteAdminListClass}
						showTableWhenEmpty={false}
						toolbar={toolbar}
					/>
					<Heading styleName="headline1">Active subscribers</Heading>
					<AdminList
						listPageHeader={true}
						entityForCaptionText={handleEntityPluralCase(channel.channelSubscribedUsers?.length, 'user')}
						showHeader={false}
						name={channel.name}
						items={subscribedUsers}
						loading={loading}
						columns={columnDefs}
						showAdd={false}
						apiCallback={gridCallback}
						overrideListClass={classes.overwriteAdminListClass}
						gridOptions={gridOptions}
					/>
				</Grid>}
				{(!isTrial && !isFreeClosedAll) && <Grid item xs={12}>
					<AdminList
						entityForCaptionText={handleEntityPluralCase(channel.channelSubscribedUsers?.length, 'user')}
						name={channel.name}
						showHeader={false}
						items={subscribedUsers}
						loading={loading}
						columns={columnDefs}
						showAdd={false}
						apiCallback={gridCallback}
						gridOptions={gridOptions}
						toolbar={toolbar}
					/>

				</Grid>}
			</Grid>
		</div>
	);
}

export default ChannelsStatsPage;
